import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Check, X, ChevronLeft, ChevronRight, Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
// v312a-fix-19 obj-safe: accept objects {url:...} and non-string values
const _toStr = (u) => {
  if (!u) return '';
  if (typeof u === 'string') return u;
  if (typeof u === 'object') return u.url || u.src || u.href || '';
  return '';
};
const imgUrl = (u) => { const s = _toStr(u); return s ? `${s.startsWith('/') ? API : ''}${s}` : ''; };
const mediaUrl = (u) => {
  const s = _toStr(u);
  if (!s) return '';
  if (s.startsWith('/')) return `${API}${s}`;
  try { return encodeURI(s); } catch { return s; }
};

// Filtert Galerie-Zähler-Müll ("1/20", "2 / 20", reine Ziffern/Slashes) aus
// den gescrapten Paragraphen – sonst stehen diese Zahlen über der Galerie.
const cleanParas = (arr) => (arr || []).filter((p) => {
  const t = (p || '').trim();
  if (!t) return false;
  if (/^[\d\s/]+$/.test(t)) return false;        // "1/20", "1/20 2/20 3/20"
  if (/^\d+\s*\/\s*\d+$/.test(t)) return false;  // "1 / 20"
  return true;
});

/**
 * Immersive "Weltmeister"-Produktstory für APR-Produkte.
 *
 * Bevorzugt die gescrapten, strukturierten `story_sections` (cinematische
 * Bänder der Originalseite: Intro, Parallax-Banner, Galerie, Szenen, Video).
 * Fällt auf das frühere Band-Layout zurück, wenn keine Story-Daten vorhanden
 * sind (Abwärtskompatibilität für noch nicht neu gescrapte Produkte).
 */
export default function AprStorySections({ product }) {
  const rootRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);

  // Filtert Galerie-Zähler-Müll ("1/20", "2 / 20", reine Ziffern/Slashes) aus
  // den gescrapten Paragraphen – sonst stehen diese Zahlen über der Galerie.
  const sections = useMemo(
    () => (product.story_sections || [])
      .map((s) => ({ ...s, paragraphs: cleanParas(s.paragraphs) }))
      .filter(
        // Heading allein reicht NICHT – sonst rendert eine inhaltslose Sektion
        // (z.B. gescraptes "Soundcheck"-Band ohne Medien) als leerer Block.
        (s) => s.paragraphs.length || (s.images || []).length || s.background_image || s.video
      ),
    [product.story_sections]
  );

  // Flache, deduplizierte Bildliste für die Lightbox (Reihenfolge = Anzeige)
  const lbImages = useMemo(() => {
    const arr = [];
    if (sections.length) {
      sections.forEach((s) => (s.images || []).forEach((i) => {
        const s2 = _toStr(i); if (s2) arr.push(s2);
      }));
    } else {
      (product.images || product.gallery_urls_full || []).forEach((i) => {
        const s = _toStr(i); if (s) arr.push(s);
      });
    }
    return [...new Set(arr)];
  }, [sections, product.images, product.gallery_urls_full]);

  // Scroll-Reveal
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const bands = root.querySelectorAll('.apr-band');
    if (!('IntersectionObserver' in window)) {
      bands.forEach((b) => b.classList.add('apr-in'));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('apr-in'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    bands.forEach((b) => obs.observe(b));
    return () => obs.disconnect();
  }, [sections.length]);

  const closeLb = useCallback(() => setLightbox(null), []);
  const nav = useCallback((dir) => {
    setLightbox((i) => {
      if (i == null) return i;
      const n = i + dir;
      if (n < 0) return lbImages.length - 1;
      if (n >= lbImages.length) return 0;
      return n;
    });
  }, [lbImages.length]);

  useEffect(() => {
    if (lightbox == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLb();
      if (e.key === 'ArrowRight') nav(1);
      if (e.key === 'ArrowLeft') nav(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, closeLb, nav]);

  const openImg = useCallback((u) => {
    const idx = lbImages.indexOf(u);
    setLightbox(idx >= 0 ? idx : null);
  }, [lbImages]);

  // Nichts Sinnvolles -> nichts rendern (Standard-Tabs greifen)
  if (!sections.length) return <LegacyStory product={product} />;

  return (
    <div className="apr-story" data-testid="apr-story-sections" ref={rootRef}>
      {sections.map((s, i) => (
        <StorySection key={i} section={s} index={i} productName={product.name}
                      brand={product.manufacturer || 'APR'} onOpenImg={openImg} />
      ))}

      {lightbox != null && lbImages[lightbox] && (
        <Lightbox images={lbImages} index={lightbox} alt={product.name}
                  onClose={closeLb} onNav={nav} />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Sections */

const SectionHead = ({ heading, eyebrow }) => (
  <>
    {eyebrow && <span className="apr-eyebrow">{eyebrow}</span>}
    <span className="apr-accent-bar" />
    {heading && <h2 className="apr-subhead" dangerouslySetInnerHTML={{ __html: headlineHtml(heading) }} />}
  </>
);

// Letztes Wort hervorheben (APR-Stil: "LOOKS AWESOME" -> awesome rot)
function headlineHtml(h) {
  const esc = (h || '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const parts = esc.trim().split(/\s+/);
  if (parts.length > 1) {
    const last = parts.pop();
    return `${parts.join(' ')} <strong>${last}</strong>`;
  }
  return `<strong>${esc}</strong>`;
}

function StorySection({ section, index, productName, brand, onOpenImg }) {
  const { layout, heading, paragraphs = [], images = [], background_image, video } = section;
  const eyebrow = index === 0 ? `${brand || 'APR'} Performance` : '';

  if (layout === 'video' && video) {
    return (
      <section className="apr-band apr-videoband" data-testid="apr-story-video">
        <div className="apr-videoband__inner">
          <SectionHead heading={heading} />
          <div className="apr-videoband__frame">
            <video controls preload="none" playsInline
                   poster={images[0] ? imgUrl(images[0]) : undefined}
                   data-testid="apr-story-video-player">
              <source src={mediaUrl(video)} type="video/mp4" />
            </video>
            <span className="apr-videoband__badge"><Play size={14} /> Video</span>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'banner' && background_image) {
    return (
      <section className="apr-band apr-cine apr-cine--parallax" data-testid="apr-story-banner"
               style={{ backgroundImage: `url(${imgUrl(background_image)})` }}>
        <div className="apr-cine__content">
          <div className="apr-cine__text">
            <SectionHead heading={heading} eyebrow={eyebrow} />
            {paragraphs.map((p, i) => <p key={i} className="apr-lead">{p}</p>)}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'gallery' && images.length) {
    return (
      <section className="apr-band apr-band--gallery" data-testid="apr-story-gallery"
               style={background_image ? { backgroundImage: `linear-gradient(rgba(10,11,14,0.92),rgba(10,11,14,0.92)), url(${imgUrl(background_image)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
        <div className="apr-band__inner">
          <SectionHead heading={heading} eyebrow={eyebrow} />
          {paragraphs.slice(0, 6).map((p, i) => <p key={i} className="apr-body apr-body--center">{p}</p>)}
          <div className="apr-gallery-grid">
            {images.map((img, i) => (
              <button key={i} className="apr-gallery-item" onClick={() => onOpenImg(img)}
                      data-testid={`apr-gallery-thumb-${index}-${i}`} aria-label={`Bild ${i + 1} öffnen`}>
                <img src={imgUrl(img)} alt={`${productName} Bild ${i + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'scene' && images.length) {
    return (
      <section className="apr-band apr-scene" data-testid="apr-story-scene"
               style={background_image ? { backgroundImage: `url(${imgUrl(background_image)})` } : undefined}>
        <div className="apr-scene__inner">
          <SectionHead heading={heading} eyebrow={eyebrow} />
          {paragraphs.map((p, i) => <p key={i} className="apr-body">{p}</p>)}
          <div className="apr-scene__grid">
            {images.map((img, i) => (
              <button key={i} className="apr-scene__item" onClick={() => onOpenImg(img)}
                      data-testid={`apr-scene-img-${index}-${i}`} aria-label={`Szene ${i + 1} öffnen`}>
                <img src={imgUrl(img)} alt={`${productName} Szene ${i + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // text / fallback – nur rendern, wenn echter Text vorhanden ist
  if (paragraphs.length) {
    return (
      <section className="apr-band apr-textband" data-testid="apr-story-text">
        <div className="apr-textband__inner">
          <SectionHead heading={heading} eyebrow={eyebrow} />
          {paragraphs.map((p, i) => <p key={i} className="apr-body">{p}</p>)}
        </div>
      </section>
    );
  }
  return null;
}

/* ---------------------------------------------------------------- Lightbox */

function Lightbox({ images, index, alt, onClose, onNav }) {
  return (
    <div className="apr-lightbox" onClick={onClose} data-testid="apr-lightbox">
      <button className="apr-lightbox__close" onClick={onClose} aria-label="Schließen"><X size={28} /></button>
      {images.length > 1 && (
        <button className="apr-lightbox__nav apr-lightbox__nav--prev"
                onClick={(e) => { e.stopPropagation(); onNav(-1); }} aria-label="Zurück"><ChevronLeft size={32} /></button>
      )}
      <img className="apr-lightbox__img" src={imgUrl(images[index])} alt={alt}
           onClick={(e) => e.stopPropagation()} />
      {images.length > 1 && (
        <button className="apr-lightbox__nav apr-lightbox__nav--next"
                onClick={(e) => { e.stopPropagation(); onNav(1); }} aria-label="Weiter"><ChevronRight size={32} /></button>
      )}
      <span className="apr-lightbox__count">{index + 1} / {images.length}</span>
    </div>
  );
}

/* ------------------------------------------------- Legacy fallback (alt) */

function LegacyStory({ product }) {
  const images = (product.images || product.gallery_urls_full || product.gallery_urls || [])
    .map(_toStr).filter(Boolean);
  const highlights = (product.highlights || []).filter(Boolean);
  let paras = (product.description_paragraphs || []).filter((p) => p && p.length > 40);
  if (!paras.length && product.description_text) {
    paras = product.description_text.split(/(?<=[.!?])\s+(?=[A-ZÄÖÜ])/).filter((p) => p.length > 40);
  }
  // Fallback: gescrapte HTML-Beschreibung (z.B. Maxton) in Absätze zerlegen
  if (!paras.length && (product.description || product.long_description)) {
    const raw = product.description || product.long_description || '';
    const text = raw
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/[ \t]+/g, ' ');
    paras = text.split(/\n+/).map((p) => p.trim()).filter((p) => p.length > 40);
  }
  const brand = product.manufacturer || 'APR';
  const [lightbox, setLightbox] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const bands = root.querySelectorAll('.apr-band');
    if (!('IntersectionObserver' in window)) { bands.forEach((b) => b.classList.add('apr-in')); return; }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('apr-in'); obs.unobserve(e.target); } });
    }, { threshold: 0.15 });
    bands.forEach((b) => obs.observe(b));
    return () => obs.disconnect();
  }, []);

  const intro = paras[0] || '';
  const detailParas = paras.slice(1, 4);
  const heroImg = images[0];
  const factImg = images[1] || images[0];
  const detailImg = images[2] || images[1] || images[0];

  const closeLb = useCallback(() => setLightbox(null), []);
  const nav = useCallback((dir) => {
    setLightbox((i) => {
      if (i == null) return i;
      const n = i + dir;
      if (n < 0) return images.length - 1;
      if (n >= images.length) return 0;
      return n;
    });
  }, [images.length]);

  useEffect(() => {
    if (lightbox == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLb();
      if (e.key === 'ArrowRight') nav(1);
      if (e.key === 'ArrowLeft') nav(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, closeLb, nav]);

  if (!intro && !highlights.length && images.length < 2) return null;

  return (
    <div className="apr-story" data-testid="apr-story-sections" ref={rootRef}>
      {(intro || heroImg) && (
        <section className="apr-band apr-band--intro" data-testid="apr-story-intro">
          <div className="apr-band__inner apr-band__inner--split">
            <div className="apr-band__text">
              <span className="apr-eyebrow">{brand} Performance</span>
              <span className="apr-accent-bar" />
              <h2 className="apr-headline">{product.name}</h2>
              {intro && <p className="apr-lead">{intro}</p>}
            </div>
            {heroImg && (
              <div className="apr-band__media">
                <img src={imgUrl(heroImg)} alt={product.name} loading="lazy" onClick={() => setLightbox(0)} />
              </div>
            )}
          </div>
        </section>
      )}

      {highlights.length >= 2 && (
        <section className="apr-band apr-band--facts" data-testid="apr-story-facts">
          <div className="apr-band__inner apr-band__inner--split">
            {factImg && (
              <div className="apr-band__media apr-band__media--frame">
                <img src={imgUrl(factImg)} alt={`${product.name} Detail`} loading="lazy"
                     onClick={() => setLightbox(images.indexOf(factImg))} />
              </div>
            )}
            <div className="apr-band__text">
              <span className="apr-accent-bar" />
              <h3 className="apr-subhead">SHORT<strong>FACTS</strong></h3>
              <p className="apr-facts-intro">Alles auf einen Blick.</p>
              <ul className="apr-facts-list">
                {highlights.map((h, i) => (
                  <li key={i}><Check size={16} className="apr-check" /> <span>{h}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {detailParas.length > 0 && (
        <section className="apr-band apr-band--detail" data-testid="apr-story-detail">
          <div className="apr-band__inner apr-band__inner--split apr-band__inner--reverse">
            <div className="apr-band__text">
              <span className="apr-accent-bar" />
              <h3 className="apr-subhead">LOOKS <strong>AWESOME</strong></h3>
              {detailParas.map((p, i) => <p key={i} className="apr-body">{p}</p>)}
            </div>
            {detailImg && (
              <div className="apr-band__media">
                <img src={imgUrl(detailImg)} alt={`${product.name} Ansicht`} loading="lazy"
                     onClick={() => setLightbox(images.indexOf(detailImg))} />
              </div>
            )}
          </div>
        </section>
      )}

      {images.length > 1 && (
        <section className="apr-band apr-band--gallery" data-testid="apr-story-gallery">
          <div className="apr-band__inner">
            <span className="apr-accent-bar" />
            <h3 className="apr-subhead">GALE<strong>RIE</strong></h3>
            <div className="apr-gallery-grid">
              {images.map((img, i) => (
                <button key={i} className="apr-gallery-item" onClick={() => setLightbox(i)}
                        data-testid={`apr-gallery-thumb-${i}`} aria-label={`Bild ${i + 1} öffnen`}>
                  <img src={imgUrl(img)} alt={`${product.name} Bild ${i + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightbox != null && images[lightbox] && (
        <Lightbox images={images} index={lightbox} alt={product.name}
                  onClose={closeLb} onNav={nav} />
      )}
    </div>
  );
}
